import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useFeatureFlag } from '@/lib/featureFlags/useFeatureFlag';

import {
  glacierPolicyApiGetAgentAuthorityOptions,
  glacierPolicyApiGetPolicyOptions,
  glacierPolicyApiListUnderwriterNotesOptions,
} from '../heyapi/@tanstack/react-query.gen';
import type {
  AgentAuthorityResponse,
  CurrentUserSchema,
} from '../heyapi/types.gen';
import { UwNotesSourceEnum } from '../heyapi/types.gen';
import {
  flattenAuthorityErrors,
  ShowAgentAuthorityNotifications,
} from '../utils/agentAuthorityNotifications';
import { useAgentAuthorityErrors } from './AgentAuthorityErrorsContext';
import { GlobalLoadingContext } from './GlobalLoadingContext';

export const useAgentAuthority = ({
  policyId,
  currentUser,
}: {
  policyId: string;
  currentUser: CurrentUserSchema | undefined;
}) => {
  const [agentAuthority, setAgentAuthority] = useState<
    AgentAuthorityResponse | undefined
  >(undefined);
  const { setAgentAuthorityErrors } = useAgentAuthorityErrors();
  const { setHeaderMessage } = useContext(GlobalLoadingContext);
  const notifiedPolicyRef = useRef<string | null>(null);

  const { data: policyData } = useQuery({
    ...glacierPolicyApiGetPolicyOptions({ path: { account_id: policyId } }),
    enabled: policyId !== undefined,
  });

  const isPolicyAssignedToMe =
    policyData?.assignedTo?.userId === currentUser?.id || undefined;
  const isPolicyReferredToMe =
    isPolicyAssignedToMe && policyData?.referredBy !== null;

  const { data: agentAuthorityData, refetch: refetchAgentAuthority } = useQuery(
    {
      enabled: policyId !== undefined,
      ...glacierPolicyApiGetAgentAuthorityOptions({
        path: {
          account_id: policyId,
        },
      }),
    }
  );

  const { data: uwNotes, isFetched: uwNotesFetched } = useQuery({
    ...glacierPolicyApiListUnderwriterNotesOptions({
      path: { account_id: policyId },
    }),
    enabled: policyId !== undefined,
  });

  const hasAgentNote = useMemo(
    () =>
      uwNotes?.some((n) => n.source === UwNotesSourceEnum.REFERRAL) ?? false,
    [uwNotes]
  );

  const showAgentAuthorityErrors = useFeatureFlag(
    'ENABLE_AGENT_AUTHORITY_ERRORS'
  );

  useEffect(() => {
    if (!agentAuthorityData || !uwNotesFetched) return;

    setAgentAuthority(agentAuthorityData);
    if (showAgentAuthorityErrors) {
      const errors = flattenAuthorityErrors(agentAuthorityData);
      setAgentAuthorityErrors(errors);

      if (notifiedPolicyRef.current !== policyId) {
        notifiedPolicyRef.current = policyId;
        ShowAgentAuthorityNotifications({
          errors,
          policyId,
          role: currentUser?.role ?? null,
          isPolicyReferredToMe,
          hasAgentNote,
          setHeaderMessage,
        });
      }
    }
  }, [
    agentAuthorityData,
    uwNotesFetched,
    setAgentAuthorityErrors,
    showAgentAuthorityErrors,
    policyId,
    setHeaderMessage,
    currentUser?.role,
    isPolicyReferredToMe,
    hasAgentNote,
  ]);

  return {
    agentAuthority,
    refetchAgentAuthority,
  };
};
